import React, { useState, useEffect, useRef } from 'react';
import { fetchAssignment } from '../services/assignmentApi';
import { getCodes, getLastName } from '../utils/assignmentHistory';
import { getRecords } from '../utils/storage';
import { getLogs } from '../services/readingLogsApi';
import { loadAudio, isAudioSaved } from '../utils/audioStorage';
import { getCloudAudioIds, getAudioSignedUrl } from '../services/audioStorageApi';
import { trackEvent } from '../utils/analytics';

const fmt = (iso) =>
  new Date(iso).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' });

const WEEK = ['日', '月', '火', '水', '木', '金', '土'];

/** きろく画面と同じ日付見出し（"YYYY-MM-DD" → "8月14日（金）"） */
function formatDay(localDate) {
  if (!localDate) return '日づけなし';
  const [y, m, d] = localDate.split('-').map(Number);
  return `${m}月${d}日（${WEEK[new Date(y, m - 1, d).getDay()]}）`;
}

const starStr = (n) => '⭐'.repeat(Math.max(0, n)) + '☆'.repeat(Math.max(0, 3 - n));

/**
 * 生徒ページ（/s）。ログイン不要。
 *
 * 配布リンクを開いた課題がここに溜まり、自分の採点履歴も見られる。
 * 課題コードは**この端末のlocalStorage**（assignmentHistory）だけが情報源。
 * サーバーの submissions は読めない（SELECTを課題の作成者だけに絞っているため）。
 *
 * 採点履歴（かだいのきろく）は、
 *  - 未ログイン・ゲスト: storage.js のローカルきろく＋端末（IndexedDB）の音声のみ
 *  - ログイン中（childIdあり）: ローカル＋クラウド（reading_logs、自分自身のデータなので
 *    submissionsとは別でRLS的にも問題ない）をマージして表示。音声も端末に無ければ
 *    クラウドの署名付きURLにフォールバックする（2026-08-19追加）。
 *    端末を変えても・localStorageを消しても、ログインしていれば課題のきろく・音声が見える。
 */
export default function StudentScreen({ onOpenAssignment, onBack, onHome, childId }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [records, setRecords]         = useState([]);

  // コード入力（リンクを配れないとき用）
  const [code, setCode]           = useState('');
  const [codeBusy, setCodeBusy]   = useState(false);
  const [codeError, setCodeError] = useState('');

  // 録音の再生。未ログインは端末のIndexedDBだけ。ログイン中はクラウドも見る
  // （2026-08-19：クラウドの課題きろくをマージするようになったのに合わせて、
  //  別の端末で録音した分の▶も出せるようにした）
  const [playingId, setPlayingId] = useState(null);
  const [cloudIds, setCloudIds]   = useState([]); // クラウド保存済み音声IDセット
  const [detailRec, setDetailRec] = useState(null); // 詳細モーダル表示中の記録
  const audioRef    = useRef(null);
  const audioUrlRef = useRef(null);

  const name = getLastName();
  // 1件でも録音（端末 or クラウド）があるか（▶の枠を出すかどうかの判定）
  const anyAudio = records.some((r) => isAudioSaved(r.id) || cloudIds.includes(r.id));

  // 見せる中身があるときだけ「詳細」を出す。
  // ⚠️ れんしゅうはローカル採点（localScore）なので details は空。feedback と transcript はある
  // ⚠️ ローカル記録は feedback/details、クラウド記録（reading_logs経由）は aiFeedback/aiDetails と
  //    フィールド名が違うので両方見る（2026-08-19：クラウドの課題きろくをマージするようになったため）
  const hasDetail = (r) => Boolean(r.transcript || r.feedback || r.details || r.aiFeedback || r.aiDetails);

  // きろく画面と同じく日付でグループ化（新しい日が上）
  const grouped = {};
  records.forEach((r) => {
    const d = r.localDate || '不明';
    (grouped[d] ??= []).push(r);
  });
  const dates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  useEffect(() => {
    // 課題の記録だけに絞る。きろくには家庭で読んだぶんも入っているが、
    // ここは「先生からの課題」のページなので混ぜない
    // （新しい順。きろくは push で積まれるので反転する）
    const localAssignmentRecords = getRecords(childId).filter((r) => r.assignmentId).reverse();
    setRecords(localAssignmentRecords);

    // ログイン中はクラウド（reading_logs）の課題きろくも合わせて見る。
    // 端末を変えた・localStorageが無い場合でも、ログインしていれば見えるように（2026-08-19追加）
    if (childId) {
      getLogs(childId).then((logs) => {
        const cloudAssignmentRecords = (logs || []).filter((r) => r.assignmentId);
        if (cloudAssignmentRecords.length === 0) return;
        setRecords((prev) => {
          const byId = new Map();
          prev.forEach((r) => byId.set(r.id, r));
          // クラウド側を優先して上書き（同じidならクラウドの内容が最新・正）
          cloudAssignmentRecords.forEach((r) => byId.set(r.id, r));
          return Array.from(byId.values())
            .sort((a, b) => (b.localDate || '').localeCompare(a.localDate || ''));
        });
      });
      // クラウド保存済みの音声ID一覧（別の端末で録音した分の▶を出すため）
      getCloudAudioIds(childId).then(setCloudIds);
    }

    const codes = getCodes(childId);
    trackEvent('ondoku_student_screen_opened', { assignment_count: codes.length });
    if (codes.length === 0) { setLoading(false); return; }

    Promise.all(codes.map((c) => fetchAssignment(c).catch(() => null)))
      .then((list) => {
        setAssignments(
          list
            .map((a, i) => (a && !a.expired ? { ...a, code: codes[i] } : null))
            .filter(Boolean)
        );
        setLoading(false);
      });
  }, [childId]);

  // 再生を止めてObjectURLも捨てる。止め方が複数あるので1か所にまとめる
  const stopAudio = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  };

  // 画面を離れるときに鳴りっぱなしにしない
  useEffect(() => () => stopAudio(), []);

  /**
   * 録音を再生する。まず端末（IndexedDB）を見て、無ければログイン中に限り
   * クラウド（署名付きURL）にフォールバックする（2026-08-19：別の端末で録音した分も
   * 聴けるように対応。未ログインはそもそもcloudIdsが空なので今まで通りローカルのみ）。
   * ⚠️ 課題の録音は AssignmentView がれんしゅう・ほんばん両方をIndexedDBに入れているので、
   *    きろく画面（ほんばんのみ）と違い、どちらのモードでも再生できる。
   */
  const handlePlay = async (rec) => {
    const wasPlaying = playingId === rec.id;
    stopAudio();
    setPlayingId(null);
    if (wasPlaying) return; // 再生中のものをもう一度押したら停止だけ

    let url = null;
    let isObjectUrl = false;

    const blob = await loadAudio(rec.id);
    if (blob) {
      url = URL.createObjectURL(blob);
      isObjectUrl = true;
    } else if (childId && cloudIds.includes(rec.id)) {
      url = await getAudioSignedUrl(rec.id, childId);
    }

    if (!url) {
      alert('ろくおんが　みつかりません。');
      return;
    }

    const audio = new Audio(url);
    audioUrlRef.current = isObjectUrl ? url : null;
    audioRef.current    = audio;
    audio.onended = () => { stopAudio(); setPlayingId(null); };
    audio.play();
    setPlayingId(rec.id);
  };

  const handleCodeOpen = async () => {
    const c = code.trim().toLowerCase();
    if (!c) return;
    setCodeBusy(true);
    setCodeError('');
    const a = await fetchAssignment(c).catch(() => null);
    setCodeBusy(false);

    if (!a)        { setCodeError('この　コードの　かだいは　みつかりません'); return; }
    if (a.expired) { setCodeError('この　かだいは　きげんぎれです'); return; }
    onOpenAssignment(c);
  };

  return (
    // 生徒ページだけ濃いティールのべた塗り（設定・きょうだい管理の濃紫と同じ扱い）。
    // うすい背景だと白いカードとの差が出ず、のっぺりして見える。
    <div className="screen assign-screen student-screen">
      {/* 上部にアプリへの導線を置く。課題やきろくが増えると下に置いたボタンは埋もれる */}
      <div className="student-nav">
        {onBack && (
          <button className="assign-mypage-btn" onClick={onBack}>← もどる</button>
        )}
        <button className="assign-mypage-btn" onClick={onHome}>🏠 ホーム</button>
      </div>

      <h2 className="assign-title">🎒 生徒ページ</h2>
      {name && <p className="assign-sub">{name}さん</p>}

      {/* ── コードを入れる（リンクを配れないとき） ───── */}
      <div className="student-section-label">🔑 コードを　いれる</div>
      <div className="assign-card">
        <p className="assign-note">せんせいから　きいた　コードを　いれてね</p>
        <input
          className="assign-name-input student-code-input"
          type="text"
          value={code}
          onChange={(e) => { setCode(e.target.value); setCodeError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleCodeOpen(); }}
          placeholder="コード（6もじ）"
          maxLength={10}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
        />
        {codeError && <p className="student-code-error">{codeError}</p>}
        <button
          className="assign-btn-primary"
          onClick={handleCodeOpen}
          disabled={codeBusy || !code.trim()}
        >
          {codeBusy ? 'さがしています…' : 'かだいを　ひらく'}
        </button>
      </div>

      {/* ── せんせいからの かだい ─────────────── */}
      <div className="student-section-label">🏫 せんせいからの かだい</div>

      {loading && <p className="assign-note">よみこみちゅう…</p>}

      {!loading && assignments.length === 0 && (
        <div className="assign-card">
          <p className="assign-note">
            まだ かだいが ありません。<br />
            せんせいから もらった リンクを ひらくと ここに ならびます
          </p>
        </div>
      )}

      {assignments.map((a) => (
        <button
          key={a.code}
          className="assign-inbox-card"
          onClick={() => onOpenAssignment(a.code)}
        >
          <span className="assign-inbox-icon">📩</span>
          <span className="assign-inbox-body">
            <span className="assign-inbox-title">{a.title || 'せんせいの かだい'}</span>
            <span className="assign-inbox-meta">{fmt(a.expires_at)}まで</span>
          </span>
          <span className="assign-inbox-arrow">›</span>
        </button>
      ))}

      {/* ── わたしの きろく ─────────────────── */}
      {/* 📖 はれんしゅうの印に使っているので、見出しは 📝 にして重複を避ける */}
      <div className="student-section-label">📝 かだいの きろく</div>

      {records.length === 0 ? (
        <div className="assign-card">
          <p className="assign-note">まだ かだいの きろくが ありません</p>
        </div>
      ) : (
        // 構造はきろく画面（RecordScreen）と同じ：日付見出し＋1件1カード。
        // 色は .student-screen 側でティールに、点数だけ大きくしてある
        <div className="student-record-days">
          {dates.map((date) => (
            <div key={date}>
              <div className="record-date-header">{formatDay(date === '不明' ? null : date)}</div>

              {grouped[date].map((r) => (
                <div key={r.id} className="record-item">
                  <div className="record-item-body">
                    <div className="record-item-title">『{r.title || 'おんどく'}』</div>
                    <div className="record-item-meta">
                      <span className={`record-mode-badge ${r.mode === 'honban' ? 'mode-honban' : 'mode-renshyu'}`}>
                        {r.mode === 'honban' ? 'ほんばん' : 'れんしゅう'}
                      </span>
                      <span className="record-stars">{starStr(r.totalStars ?? 0)}</span>
                      <span className="record-score">{r.avgScore ?? '—'}てん</span>
                    </div>
                  </div>

                  <div className="student-record-actions">
                    {hasDetail(r) && (
                      <button
                        className="record-detail-btn"
                        onClick={() => setDetailRec(r)}
                        title="さいてんの　くわしいないようを　みる"
                      >
                        くわしく
                      </button>
                    )}
                    {/* ▶の有無で「くわしく」の位置がズレないよう、1件でも録音があれば
                        全カードに同じ幅の枠を置く。録音が1件も無ければ枠ごと出さない */}
                    {anyAudio && (
                      <span className="student-record-play">
                        {(isAudioSaved(r.id) || cloudIds.includes(r.id)) && (
                          <button
                            className={`record-play-btn ${playingId === r.id ? 'playing' : ''}`}
                            onClick={() => handlePlay(r)}
                            title={cloudIds.includes(r.id) && !isAudioSaved(r.id) ? '☁️ クラウドから再生' : 'ろくおんを　きく'}
                          >
                            {playingId === r.id ? '⏹' : (cloudIds.includes(r.id) && !isAudioSaved(r.id) ? '☁️' : '▶')}
                          </button>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* ── さいてんの詳細 ─────────────────── */}
      {/* きろく画面と同じモーダルを使い回す。色だけ .student-screen 側で学校モードに寄せている。
          ⚠️ ローカル記録（AssignmentViewがlocalStorageに入れたもの）はfeedback/details、
             クラウド記録（reading_logs経由、2026-08-19マージ対応）はaiFeedback/aiDetailsと
             フィールド名が違うので両方見る */}
      {detailRec && (
        <div className="record-detail-overlay" onClick={() => setDetailRec(null)}>
          <div className="record-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="record-detail-header">
              <div className="record-detail-title">『{detailRec.title || 'おんどく'}』</div>
              <div className="record-detail-meta">
                <span className="record-detail-score">{detailRec.avgScore ?? '—'}てん</span>
                <span className="record-detail-stars">{starStr(detailRec.totalStars ?? 0)}</span>
              </div>
            </div>

            {(detailRec.feedback || detailRec.aiFeedback) && (
              <div className="record-detail-section">
                {/* ほんばんはClaude採点、れんしゅうはローカル採点なので
                    「AIから」とは書かず、どちらでも正しい言い方にする */}
                <div className="record-detail-section-label">💬 ひとこと</div>
                <div className="record-detail-feedback">{detailRec.feedback || detailRec.aiFeedback}</div>
              </div>
            )}

            {(detailRec.details || detailRec.aiDetails) && (
              <div className="record-detail-section">
                <div className="record-detail-section-label">📝 くわしいコメント</div>
                <div className="record-detail-comment">{detailRec.details || detailRec.aiDetails}</div>
              </div>
            )}

            {detailRec.transcript && (
              <div className="record-detail-section">
                <div className="record-detail-section-label">🎤 よみとった文</div>
                <div className="record-detail-transcript">{detailRec.transcript}</div>
              </div>
            )}

            <button className="record-detail-close" onClick={() => setDetailRec(null)}>
              とじる
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
